// Conversational flow: one question per screen, full-viewport, auto-advance
// on single-choice taps, Enter advances, thin progress bar, back arrow.
// Enabled by definition-level "flow": "conversational"; the wizard mode in
// App.tsx is untouched.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Answers,
  DescriptionItem,
  FieldItem,
  FormDefinition,
  OTHER,
} from './types';
import { fieldForRender, validateFields } from './logic';
import { FieldView, rendersAsCards } from './FieldView';

interface Screen {
  id: string;
  /** Hero/info screen (description item): rendered big with a CTA. */
  hero?: DescriptionItem;
  title?: string;
  help?: string;
  fields: FieldItem[];
  inlineDescs: DescriptionItem[];
  /** presentation:"prompt" blocks: fired as a modal when Continue is pressed. */
  prompts?: DescriptionItem[];
  /** Single question screen: field label becomes the big question. */
  single: boolean;
  /** Single-choice card screens advance ~250ms after a tap. */
  autoAdvance: boolean;
  /** The restored-details confirm screen (carries the "Not you?" action). */
  review?: boolean;
  /**
   * A link-prefilled answer shown for confirmation (definition prefillConfirm):
   * the answer is already selected, so Continue shows even on an auto-advance
   * screen, and tapping a different answer still moves on by itself.
   */
  confirmPrefill?: boolean;
}

function buildScreens(
  def: FormDefinition,
  visible: Set<string>,
  prefilled: Set<string>,
  restored: Set<string> = new Set(),
): Screen[] {
  const out: Screen[] = [];
  const showable = (f: FieldItem) => visible.has(f.id) && !f.hidden;
  // Prefilled fields skip their screen, except prefillConfirm ones, which are
  // shown with the answer already selected (a star tapped in an email can be
  // a slip, and a skipped screen leaves no way to change it).
  const confirmIds = new Set(def.prefillConfirm ?? []);
  const skipPrefilled = (f: FieldItem) => prefilled.has(f.id) && !confirmIds.has(f.id);
  const confirming = (f: FieldItem) => prefilled.has(f.id) && confirmIds.has(f.id);

  // Details restored from this device collapse into ONE confirm screen at the
  // point the first of them would have been asked, and their own screens are
  // skipped. Nothing restored is ever submitted without the customer seeing
  // it here first.
  const reviewIds = new Set((def.remember?.review ?? []).filter((id) => restored.has(id)));
  let reviewEmitted = reviewIds.size === 0;
  const reviewFields: FieldItem[] = [];
  if (!reviewEmitted) {
    for (const step of def.steps) {
      for (const it of step.items) {
        const candidates: FieldItem[] =
          it.kind === 'field' ? [it]
            : it.kind === 'section'
              ? it.items.filter((c): c is FieldItem => c.kind === 'field')
              : [];
        for (const f of candidates) if (reviewIds.has(f.id)) reviewFields.push(f);
      }
    }
    if (reviewFields.length === 0) reviewEmitted = true;
  }
  const emitReview = () => {
    reviewEmitted = true;
    out.push({
      id: '__review__',
      title: 'Are these still right?',
      help: 'We saved these from your last order on this device.',
      fields: reviewFields,
      inlineDescs: [],
      single: false,
      autoAdvance: false,
      review: true,
    });
  };

  for (const step of def.steps) {
    for (const it of step.items) {
      if (!visible.has(it.id)) continue;
      // The confirm screen takes the place of the first remembered question.
      if (!reviewEmitted) {
        const owns =
          (it.kind === 'field' && reviewIds.has(it.id)) ||
          (it.kind === 'section' &&
            it.items.some((c) => c.kind === 'field' && reviewIds.has(c.id)));
        if (owns) emitReview();
      }
      if (it.kind === 'description') {
        out.push({
          id: it.id,
          hero: it,
          fields: [],
          inlineDescs: [],
          single: false,
          autoAdvance: false,
        });
      } else if (it.kind === 'field') {
        if (it.hidden || skipPrefilled(it) || reviewIds.has(it.id)) continue;
        out.push({
          id: it.id,
          title: it.label,
          help: it.help,
          fields: [it],
          inlineDescs: [],
          single: true,
          autoAdvance: rendersAsCards(it) || it.type === 'stars',
          confirmPrefill: confirming(it),
        });
      } else {
        const fields = it.items.filter(
          (c): c is FieldItem => c.kind === 'field' && showable(c) && !reviewIds.has(c.id),
        );
        const descs = it.items.filter(
          (c): c is DescriptionItem => c.kind === 'description' && visible.has(c.id),
        );
        // 'prompt' blocks are gates on Continue, never page furniture, so they
        // are held back from the inline list and fired by advance().
        const inlineDescs = descs.filter((c) => c.presentation !== 'prompt');
        const prompts = descs.filter((c) => c.presentation === 'prompt');
        if (fields.length === 0 && inlineDescs.length === 0 && prompts.length === 0) continue;
        // A screen whose every field arrived prefilled is skipped entirely.
        if (fields.length > 0 && fields.every(skipPrefilled)) continue;
        out.push({
          id: it.id,
          title: it.title,
          help: it.description,
          fields,
          inlineDescs,
          prompts,
          single: false,
          autoAdvance: false,
        });
      }
    }
  }
  return out;
}

export interface ConversationalProps {
  def: FormDefinition;
  visibleItems: Set<string>;
  /** Field ids satisfied by URL-parameter prefill; their screens are skipped. */
  prefilled: Set<string>;
  /** Field ids restored from this device; the `review` subset gets a confirm screen. */
  restored: Set<string>;
  /** "Not you?" on the confirm screen: forget this device and start clean. */
  onForget: () => void;
  answers: Answers;
  otherText: Record<string, string>;
  confirmEmail: Record<string, string>;
  errors: Record<string, string>;
  banner: string | null;
  submitting: boolean;
  onValue: (id: string, value: string | string[]) => void;
  onOtherText: (id: string, text: string) => void;
  onConfirm: (id: string, text: string) => void;
  setErrors: (errors: Record<string, string>) => void;
  submit: () => void;
}

export function Conversational(props: ConversationalProps) {
  const { def, visibleItems, answers, otherText, confirmEmail, errors } = props;

  const screens = useMemo(
    () => buildScreens(def, visibleItems, props.prefilled, props.restored),
    [def, visibleItems, props.prefilled, props.restored],
  );
  const [cursorId, setCursorId] = useState<string | null>(null);
  const [direction, setDirection] = useState<'fwd' | 'back'>('fwd');
  const autoTimer = useRef<number | null>(null);

  const index = Math.max(
    0,
    cursorId ? screens.findIndex((s) => s.id === cursorId) : 0,
  );
  const screen = screens[Math.min(index, screens.length - 1)];
  const isLast = index >= screens.length - 1;

  const clearAutoTimer = useCallback(() => {
    if (autoTimer.current !== null) {
      window.clearTimeout(autoTimer.current);
      autoTimer.current = null;
    }
  }, []);

  useEffect(() => clearAutoTimer, [clearAutoTimer]);

  /** presentation:"info" sheet currently open (id), and the pending prompt. */
  const [openInfo, setOpenInfo] = useState<string | null>(null);
  const [openPrompt, setOpenPrompt] = useState<DescriptionItem | null>(null);
  /** Prompts already answered, so Continue is never gated on them twice. */
  const seenPrompts = useRef<Set<string>>(new Set());

  const goNext = useCallback(() => {
    props.setErrors({});
    if (isLast) {
      props.submit();
      return;
    }
    setDirection('fwd');
    setCursorId(screens[index + 1].id);
    window.scrollTo({ top: 0 });
  }, [isLast, screens, index, props]);

  const advance = useCallback(() => {
    clearAutoTimer();
    if (!screen) return;
    const errs = validateFields(screen.fields, answers, otherText, confirmEmail);
    if (Object.keys(errs).length > 0) {
      props.setErrors(errs);
      return;
    }
    // Gate: a visible prompt for this screen that has not been answered yet
    // opens as a modal INSTEAD of moving on. It is asked once per screen
    // visit, so a customer who says no thanks is not nagged on the way back.
    const pending = (screen.prompts ?? []).find((p) => !seenPrompts.current.has(p.id));
    if (pending) {
      props.setErrors({});
      setOpenPrompt(pending);
      return;
    }
    goNext();
  }, [screen, answers, otherText, confirmEmail, props, clearAutoTimer, goNext]);

  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  // Autofocus the first control on each screen so typing can start straight
  // away. Fine-pointer (keyboard) devices only: iOS/iPadOS suppress the
  // keyboard on programmatic focus, leaving a confusing focused-but-silent
  // caret and risking layout jumps; on touch, a tap focuses natively.
  // Keyed on the screen ID, NOT the screen object: visibility recomputes on
  // every keystroke, so the object identity changes as you type. Depending on
  // it re-armed this timer on each character and then yanked focus back to the
  // FIRST input — typing your surname jumped you to your first name.
  const screenId = screen?.id;
  const screenIsHero = Boolean(screen?.hero);
  useEffect(() => {
    if (!screenId || screenIsHero) return;
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const id = window.setTimeout(() => {
      // Never steal focus from a control the customer is already using.
      const active = document.activeElement;
      if (active && active.closest?.('.conv-screen')) return;
      const el = document.querySelector<HTMLElement>(
        '.conv-screen input:not(.hp), .conv-screen textarea, .conv-screen select',
      );
      el?.focus({ preventScroll: true });
    }, 320);
    return () => window.clearTimeout(id);
  }, [screenId, screenIsHero]);

  const goBack = useCallback(() => {
    clearAutoTimer();
    if (index === 0) return;
    props.setErrors({});
    setDirection('back');
    setCursorId(screens[index - 1].id);
    window.scrollTo({ top: 0 });
  }, [index, screens, props, clearAutoTimer]);

  // ---- swipe-right to go back -------------------------------------------
  // Horizontal-intent only (dx > 60px and |dx| > 2|dy|), single touch, never
  // over a focused control (keyboard open), never forward. Refs only during
  // the gesture, no state updates, no preventDefault: scrolling and card
  // taps are untouched and the listeners stay passive.
  const swipeStart = useRef<{ x: number; y: number } | null>(null);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) {
      swipeStart.current = null;
      return;
    }
    const target = e.target as HTMLElement | null;
    if (target?.closest('input, select, textarea')) {
      swipeStart.current = null;
      return;
    }
    swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      if (!start || e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - start.x;
      const dy = e.changedTouches[0].clientY - start.y;
      if (dx > 60 && Math.abs(dx) > 2 * Math.abs(dy)) goBack();
    },
    [goBack],
  );

  /** Wrap onValue: single-choice card taps advance shortly after. */
  const handleValue = useCallback(
    (id: string, value: string | string[]) => {
      props.onValue(id, value);
      if (
        screen &&
        screen.autoAdvance &&
        screen.single &&
        screen.fields[0]?.id === id &&
        typeof value === 'string' &&
        value !== '' &&
        value !== OTHER
      ) {
        clearAutoTimer();
        autoTimer.current = window.setTimeout(() => advanceRef.current(), 250);
      }
    },
    [screen, props, clearAutoTimer],
  );

  // After a failed submit (server 422 or an earlier screen broke), jump to the
  // first screen that carries an error.
  useEffect(() => {
    const keys = Object.keys(errors);
    if (keys.length === 0 || !screen) return;
    const hasError = (s: Screen) =>
      s.fields.some((f) => errors[f.id] || errors[`${f.id}__confirm`]);
    if (hasError(screen)) return;
    const target = screens.find(hasError);
    if (target && target.id !== screen.id) {
      setDirection('back');
      setCursorId(target.id);
    }
  }, [errors, screen, screens]);

  /**
   * Skip: for screens whose fields are all optional, advance while leaving
   * (or making) them empty. Never validates, never submits, so it can never
   * block; clearing avoids carrying half-typed values forward.
   */
  const skip = useCallback(() => {
    clearAutoTimer();
    if (!screen || isLast) return;
    for (const f of screen.fields) {
      props.onValue(f.id, f.type === 'checkbox' ? [] : '');
    }
    props.setErrors({});
    setDirection('fwd');
    setCursorId(screens[index + 1].id);
    window.scrollTo({ top: 0 });
  }, [screen, screens, index, isLast, props, clearAutoTimer]);

  if (!screen) return null;

  const skippable =
    !screen.hero &&
    !isLast &&
    screen.fields.length > 0 &&
    screen.fields.every((f) => !f.required);

  // ---- hero screen: full-bleed dark welcome with its own chrome ----
  if (screen.hero && screen.hero.presentation === 'hero') {
    const hero = screen.hero;
    return (
      <div className="conv-hero-screen">
        {hero.background && (
          <div
            className="conv-hero-bg"
            style={{ backgroundImage: `url(${hero.background})` }}
            aria-hidden="true"
          />
        )}
        <div className="conv-hero-scrim" aria-hidden="true" />
        {def.logo && (
          <div className="conv-hero-top">
            <img className="conv-hero-logo" src={def.logo} alt="" width={52} height={52} />
          </div>
        )}
        <div className="conv-hero-content">
          <div
            className="conv-hero-copy"
            dangerouslySetInnerHTML={{ __html: hero.html }}
          />
          <button
            type="button"
            className="btn btn-primary btn-block conv-cta-btn conv-hero-cta"
            onClick={advance}
          >
            {hero.agreeLabel ?? 'Get started'}
          </button>
        </div>
      </div>
    );
  }

  const infoItem = openInfo
    ? screen.inlineDescs.find((d) => d.id === openInfo) ?? null
    : null;

  const showCta = !screen.autoAdvance || isLast || !!screen.confirmPrefill;
  const ctaLabel = props.submitting
    ? 'Sending...'
    : screen.hero
      ? screen.hero.agreeLabel ?? 'Continue'
      : isLast
        ? def.submitLabel ?? 'Submit'
        : 'Continue';

  return (
    <div className="conv">
      <header className="conv-top">
        {index > 0 ? (
          <button type="button" className="conv-back" aria-label="Back" onClick={goBack}>
            {/* TEXT glyph, not SVG: a text node with inline color cannot be
                blanked by any cascade, barrier, or SVG rendering quirk. */}
            <span
              aria-hidden="true"
              style={{
                color: '#1A1A1A',
                fontSize: '20px',
                lineHeight: 1,
                fontFamily: 'Arial, sans-serif',
                display: 'block',
                transform: 'translateY(-1px)',
              }}
            >
              {'←'}
            </span>
          </button>
        ) : (
          <span className="conv-back-spacer" aria-hidden="true" />
        )}
        {def.logo && (
          <img className="conv-header-logo" src={def.logo} alt="" width={26} height={26} />
        )}
        <div
          className="conv-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={screens.length}
          aria-valuenow={index + 1}
        >
          {/* scaleX, not width: transform-only animation stays off the layout path */}
          <div
            className="conv-progress-fill"
            style={{ transform: `scaleX(${(index + 1) / screens.length})` }}
          />
        </div>
      </header>

      <form
        key={screen.id}
        className={`conv-screen ${direction === 'fwd' ? 'conv-in-fwd' : 'conv-in-back'}`}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          advance();
        }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="conv-body">
          {screen.hero ? (
            <div
              className="conv-hero"
              dangerouslySetInnerHTML={{ __html: screen.hero.html }}
            />
          ) : (
            <>
              {screen.title && <h1 className="conv-question">{screen.title}</h1>}
              {screen.help && <p className="conv-help">{screen.help}</p>}
              <div className="conv-controls">
                {screen.fields.map((f) => (
                  <FieldView
                    key={f.id}
                    field={fieldForRender(f, answers, otherText)}
                    value={answers[f.id]}
                    otherText={otherText[f.id] ?? ''}
                    confirmValue={confirmEmail[f.id] ?? ''}
                    error={errors[f.id]}
                    confirmError={errors[`${f.id}__confirm`]}
                    hideLabel={screen.single}
                    chips={f.type === 'checkbox'}
                    onValue={handleValue}
                    onOtherText={props.onOtherText}
                    onConfirm={props.onConfirm}
                  />
                ))}
                {screen.inlineDescs.map((d) =>
                  // 'info' and 'expander' are the compact forms: a long
                  // explainer beside a field (Lab vs Flat) would otherwise
                  // bury the question it belongs to.
                  d.presentation === 'info' || d.presentation === 'expander' ? (
                    <button
                      key={d.id}
                      type="button"
                      className="info-btn"
                      onClick={() => setOpenInfo(d.id)}
                    >
                      <span className="info-btn-icon" aria-hidden="true">
                        i
                      </span>
                      <span className="info-btn-label">
                        {d.summary ?? 'More information'}
                      </span>
                    </button>
                  ) : (
                    <div
                      key={d.id}
                      className="description"
                      dangerouslySetInnerHTML={{ __html: d.html }}
                    />
                  ),
                )}
                {screen.review && (
                  <button type="button" className="conv-forget" onClick={props.onForget}>
                    Not you? Clear these details
                  </button>
                )}
              </div>
            </>
          )}

          {props.banner && (
            <div className="banner" role="alert">
              {props.banner}
            </div>
          )}
        </div>

        <footer className="conv-cta">
          {showCta && (
            <button
              type="submit"
              className="btn btn-primary btn-block conv-cta-btn"
              disabled={props.submitting}
            >
              {ctaLabel}
            </button>
          )}
          {skippable && (
            <div className="conv-skip-row">
              <button type="button" className="link-btn" onClick={skip}>
                Skip
              </button>
            </div>
          )}
        </footer>
      </form>

      {/* presentation:"info" — the explainer, on demand */}
      {infoItem && (
        <div className="dialog-overlay" role="presentation">
          <div className="dialog-sheet" role="dialog" aria-modal="true">
            <div
              className="description dialog-body"
              dangerouslySetInnerHTML={{ __html: infoItem.html }}
            />
            <div className="dialog-actions">
              <button
                type="button"
                className="btn btn-primary btn-block"
                onClick={() => setOpenInfo(null)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* presentation:"prompt" — the gate on Continue. Every button lets the
          customer through; the difference is only what gets answered first. */}
      {openPrompt && (
        <div className="dialog-overlay" role="presentation">
          <div className="dialog-sheet" role="dialog" aria-modal="true">
            <div
              className="description dialog-body"
              dangerouslySetInnerHTML={{ __html: openPrompt.html }}
            />
            <div className="dialog-actions dialog-actions-stack">
              {(openPrompt.actions ?? []).map((a, i) => (
                <button
                  key={a.label}
                  type="button"
                  className={`btn btn-block ${i === 0 ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => {
                    seenPrompts.current.add(openPrompt.id);
                    if (a.setField && a.value !== undefined) {
                      props.onValue(a.setField, a.value);
                    }
                    setOpenPrompt(null);
                    goNext();
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
